import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useToast } from "../context/ToastContext";
import { createLabourSupplier, type LabourSupplierMaster } from "../api/labourSuppliersApi";

const emptyDraft = (): Omit<LabourSupplierMaster, "id" | "status" | "createdAt"> => ({
  contactPersonName: "",
  firmName: "",
  gstNumber: "",
  contactPhone: "",
  contactEmail: "",
  registeredAddress: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankName: "",
  bankAccountName: "",
});

export function LabourSupplierOnboardingPage() {
  const { showToast } = useToast();
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.firmName.trim() || !draft.contactPersonName.trim()) {
      showToast("Firm name and contact person are required.", "error");
      return;
    }
    setSaving(true);
    try {
      await createLabourSupplier(draft);
      showToast("Labour supplier saved.", "success");
      setDraft(emptyDraft());
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not save labour supplier.", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Invoice", to: "/bills/inward" },
          { label: "Labour suppliers" },
        ]}
        title="Labour supplier master"
        subtitle="Register labour suppliers used on labour invoices."
        actions={
          <Link className="btn btn--tg-secondary btn--sm" to="/bills/inward">
            Back to invoices
          </Link>
        }
      />
      <form className="card card--elevated" onSubmit={(e) => void handleSave(e)}>
        <div className="goods-receipts-filters__grid">
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">Firm name *</span>
            <input
              className="form-control"
              value={draft.firmName}
              onChange={(e) => setDraft((d) => ({ ...d, firmName: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">Contact person *</span>
            <input
              className="form-control"
              value={draft.contactPersonName}
              onChange={(e) => setDraft((d) => ({ ...d, contactPersonName: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">Phone</span>
            <input
              className="form-control"
              value={draft.contactPhone}
              onChange={(e) => setDraft((d) => ({ ...d, contactPhone: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">Email</span>
            <input
              className="form-control"
              type="email"
              value={draft.contactEmail}
              onChange={(e) => setDraft((d) => ({ ...d, contactEmail: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">GSTIN</span>
            <input
              className="form-control"
              value={draft.gstNumber}
              onChange={(e) => setDraft((d) => ({ ...d, gstNumber: e.target.value.toUpperCase() }))}
            />
          </label>
          <label className="goods-receipts-filters__field goods-receipts-filters__field--wide">
            <span className="goods-receipts-filters__label">Registered address</span>
            <textarea
              className="form-control"
              rows={2}
              value={draft.registeredAddress}
              onChange={(e) => setDraft((d) => ({ ...d, registeredAddress: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">Bank account name</span>
            <input
              className="form-control"
              value={draft.bankAccountName}
              onChange={(e) => setDraft((d) => ({ ...d, bankAccountName: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">Bank name</span>
            <input
              className="form-control"
              value={draft.bankName}
              onChange={(e) => setDraft((d) => ({ ...d, bankName: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">Account number</span>
            <input
              className="form-control"
              value={draft.bankAccountNumber}
              onChange={(e) => setDraft((d) => ({ ...d, bankAccountNumber: e.target.value }))}
            />
          </label>
          <label className="goods-receipts-filters__field">
            <span className="goods-receipts-filters__label">IFSC</span>
            <input
              className="form-control"
              value={draft.bankIfsc}
              onChange={(e) => setDraft((d) => ({ ...d, bankIfsc: e.target.value.toUpperCase() }))}
            />
          </label>
        </div>
        <div className="inv-outward-issue__actions" style={{ marginTop: "1rem" }}>
          <button type="submit" className="btn btn--primary btn--tg" disabled={saving}>
            {saving ? "Saving…" : "Save labour supplier"}
          </button>
        </div>
      </form>
    </>
  );
}
